import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Port of zelusSeva packages/auth-core password-policy.ts — keep in sync with the API.
export const PASSWORD_RULES = [
  'length',
  'lowercase',
  'uppercase',
  'digit',
  'symbol',
  'not-contain-username',
] as const;

export type PasswordRule = (typeof PASSWORD_RULES)[number];

export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordPolicyContext {
  readonly username: string;
}

export interface PasswordPolicyResult {
  readonly ok: boolean;
  readonly missing: readonly PasswordRule[];
}

export const PASSWORD_RULE_LABELS: Record<PasswordRule, string> = {
  length: `At least ${MIN_PASSWORD_LENGTH} characters`,
  lowercase: 'A lowercase letter',
  uppercase: 'An uppercase letter',
  digit: 'A number',
  symbol: 'A symbol',
  'not-contain-username': 'Does not contain your username',
};

const SYMBOL_RE = /[^a-zA-Z0-9]/;
const LOWER_RE = /[a-z]/;
const UPPER_RE = /[A-Z]/;
const DIGIT_RE = /[0-9]/;

export function validatePassword(pw: string, ctx: PasswordPolicyContext): PasswordPolicyResult {
  const missing: PasswordRule[] = [];
  if (pw.length < MIN_PASSWORD_LENGTH) missing.push('length');
  if (!LOWER_RE.test(pw)) missing.push('lowercase');
  if (!UPPER_RE.test(pw)) missing.push('uppercase');
  if (!DIGIT_RE.test(pw)) missing.push('digit');
  if (!SYMBOL_RE.test(pw)) missing.push('symbol');
  if (ctx.username && pw.toLowerCase().includes(ctx.username.toLowerCase())) {
    missing.push('not-contain-username');
  }
  return { ok: missing.length === 0, missing };
}

/** Reactive-forms validator; error key 'passwordPolicy' with value { missing }. */
export function passwordPolicyValidator(username: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { ok, missing } = validatePassword(String(control.value ?? ''), {
      username: username(),
    });
    return ok
      ? null
      : {
          passwordPolicy: {
            missing,
            message: 'Password needs: ' + missing.map((r) => PASSWORD_RULE_LABELS[r]).join(', '),
          },
        };
  };
}

/** Group validator: error key 'passwordMismatch' when newPassword !== confirmPassword. */
export function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPassword = group.get('newPassword')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  if (!newPassword || !confirmPassword) return null;
  return newPassword === confirmPassword ? null : { passwordMismatch: true };
}
